import { Socket } from "socket.io-client";

import { MsEngineApi } from '@microtica/ms-engine-sdk';
import { MsApApi } from '@microtica/ms-ap-sdk';
import { MsUserApi } from "@microtica/ms-usermanagement-sdk";
import { MsProjectApi } from "@microtica/ms-project-sdk";
import { MsNotificationApi } from "@microtica/ms-notification-sdk";
import { ComponentInstanceSchedulerApi } from "@microtica/ms-cloud-cost-optimizer-sdk";
import { MsKubeApi } from "@microtica/ms-kube-sdk";
import { MsAwsApi } from "@microtica/ms-aws-sdk";
import { MsElasticsearchApi } from "@microtica/ms-elasticsearch-sdk";

type WindowConfig = Window & { env: { BACKEND_ENDPOINT: string } }

export function getBaseUrl(): string {
    if ((window as unknown as WindowConfig).env) {
        return (window as unknown as WindowConfig).env.BACKEND_ENDPOINT;
    } else {
        return "http://localhost:80"
    }
};

export function getIdToken(): string | undefined {
    if (process.env.NODE_ENV === "test") {
        return "testing_access_token";
    }
    return localStorage.getItem("idToken") || undefined;
}

export function getEngineAPI() {
    return new MsEngineApi({
        basePath: `${getBaseUrl()}/ms-engine`,
        accessToken: getIdToken()
    });
}

export function getContinuousIntegrationAPI() {
    return new MsApApi({
        basePath: `${getBaseUrl()}/ms-ap`,
        accessToken: getIdToken()
    });
}

export function getNotificationAPI() {
    return new MsNotificationApi({
        basePath: `${getBaseUrl()}/ms-notification`,
        accessToken: getIdToken()
    });
}

export function getUserManagementAPI() {
    return new MsUserApi({
        basePath: `${getBaseUrl()}/ms-usermanagement`,
        accessToken: getIdToken()
    });
}

export function getProjectAPI() {
    return new MsProjectApi({
        basePath: `${getBaseUrl()}/ms-project`,
        accessToken: getIdToken()
    });
}

export function getCloudCostOptimizerAPI() {
    return new ComponentInstanceSchedulerApi({
        basePath: `${getBaseUrl()}/ms-cloud-cost-optimizer`,
        accessToken: getIdToken()
    });
}

export function getAwsAPI() {
    return new MsAwsApi({
        basePath: `${getBaseUrl()}/ms-aws`,
        accessToken: getIdToken()
    });
}

export function getKubeAPI() {
    return new MsKubeApi({
        basePath: `${getBaseUrl()}/ms-kube`,
        accessToken: getIdToken()
    });
}

export function getElasticSearchAPI() {
    return new MsElasticsearchApi({
        basePath: `${getBaseUrl()}/ms-elasticsearch`,
        accessToken: getIdToken()
    });
}

export function socketIO(queryParams: any) {
    return new RealtimePollingSocket(
        getBaseUrl(),
        queryParams,
        getIdToken()
    ) as unknown as Socket;
}

export function disconnectSocketIO() {
    return undefined;
}

type RealtimeListener = (...args: any[]) => void;

class RealtimePollingSocket {
    private listeners: { [event: string]: RealtimeListener[] } = {};
    private pendingLogs: any[] = [];
    private controller = new AbortController();
    private disconnected = false;
    private disconnectPending = false;

    constructor(
        private baseUrl: string,
        private queryParams: Record<string, any>,
        private token?: string
    ) {
        this.poll();
    }

    on(event: string, listener: RealtimeListener) {
        this.listeners[event] = (this.listeners[event] || []).concat(listener);
        if (event === "logs" && this.pendingLogs.length) {
            this.pendingLogs.splice(0).forEach(payload => listener(payload));
        }
        if (event === "disconnect" && this.disconnectPending) {
            this.disconnectPending = false;
            listener("server namespace disconnect");
        }
        return this;
    }

    off(event: string, listener?: RealtimeListener) {
        if (!listener) this.listeners[event] = [];
        else this.listeners[event] = (this.listeners[event] || []).filter(item => item !== listener);
        return this;
    }

    disconnect() {
        this.finish();
        return this;
    }

    close() {
        return this.disconnect();
    }

    private emit(event: string, payload?: any) {
        const listeners = this.listeners[event] || [];
        if (!listeners.length) {
            if (event === "logs") this.pendingLogs.push(payload);
            if (event === "disconnect") this.disconnectPending = true;
            return;
        }
        listeners.forEach(listener => listener(payload));
    }

    private finish() {
        if (this.disconnected) return;
        this.disconnected = true;
        this.controller.abort();
        this.emit("disconnect", "server namespace disconnect");
    }

    private async poll() {
        const type = String(this.queryParams.typeOfLogs || "");
        let cursor: string | undefined;

        try {
            while (!this.disconnected) {
                const url = new URL(`${this.baseUrl.replace(/\/$/, "")}/ms-realtime/logs`);
                url.searchParams.set("type", type);
                if (cursor) url.searchParams.set("cursor", cursor);
                Object.keys(this.queryParams).forEach(key => {
                    const value = this.queryParams[key];
                    if (key !== "typeOfLogs" && value !== undefined) {
                        url.searchParams.set(key, String(value));
                    }
                });

                const response = await fetch(url.toString(), {
                    credentials: "include",
                    headers: { Authorization: `Bearer ${this.token}` },
                    signal: this.controller.signal
                });
                if (!response.ok) throw new Error(`logs poll failed: ${response.status}`);

                const payload: {
                    events?: any[];
                    nextCursor?: string | null;
                    done?: boolean;
                } = await response.json();
                const events = Array.isArray(payload.events) ? payload.events : [];

                if (type === "pipeline") {
                    events.forEach(event => this.emit("logs", event));
                } else if (type === "terraform") {
                    if (events.length) this.emit("logs", events);
                } else if (type === "service") {
                    this.emit("logs", { logs: events });
                }

                if (payload.done) break;
                cursor = payload.nextCursor || undefined;
                await new Promise(resolve => setTimeout(resolve, 3000));
            }
        } catch (err) {
            if (!this.controller.signal.aborted) console.error("Realtime logs polling failed", err);
        } finally {
            this.finish();
        }
    }
}
